import { useCallback, useMemo, useState } from "react";
import { validateEmail } from "#app/lib/custom-field-utils";
import {
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldLabel,
  Icon,
  ScrollArea,
  Text,
  Textarea,
  useIsTouchDevice,
} from "@salestrak/ui";

import { RoleSelector } from "#app/components/entity-selectors/role-selector";
import { useRolePool } from "#app/components/entity-selectors/hooks/use-role-pool";
import { TeamSelector } from "#app/components/entity-selectors/team-selector";
import { useAppData } from "#app/lib/data";
import { useInvitableRoles } from "#app/lib/hooks/use-invitable-roles";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useWorkspace } from "#app/lib/data/app-provider";
import type { Omittable } from "@salestrak/ui";

type InviteMembersDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (count: number) => void;
  onError?: (message: string) => void;
}>;

/** Per-invite overrides; null fields fall back to the compose view's defaults. */
interface InviteOverride {
  role: string | null;
  teams: string[] | null;
}

type DialogMode = "compose" | "review";

const EMAIL_SPLIT = /[\s,]+/;

export function InviteMembersDialog({
  open,
  onOpenChange,
  onSuccess,
  onError,
}: InviteMembersDialogProps) {
  // Touch keyboards pop open and cover the dialog when the message field
  // grabs focus on open, so touch devices open with no autofocus.
  const isTouchDevice = useIsTouchDevice();
  useAppData(); // touch context for parity with other invite-dialog mounts
  const { inviteMembers } = useMemberMutations();
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? "";

  const [mode, setMode] = useState<DialogMode>("compose");
  const [emailsText, setEmailsText] = useState("");
  const [overrides, setOverrides] = useState<Record<string, InviteOverride>>(
    {},
  );
  const [mainRole, setMainRole] = useState<string | null>(null);
  const [mainTeamIds, setMainTeamIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { allowedRoleIds } = useInvitableRoles(workspaceId);
  const { defaultRoleId } = useRolePool({ allowedRoleIds });
  const composeRoleId = mainRole ?? defaultRoleId;

  // The textarea is the source of truth — emails are parsed in place,
  // never stripped out of the text. Deduped case-insensitively.
  const validEmails = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const segment of emailsText.split(EMAIL_SPLIT)) {
      if (!segment || !validateEmail(segment)) continue;
      const key = segment.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(segment);
    }
    return out;
  }, [emailsText]);

  // Overrides are keyed by lowercased email so re-casing an address in the
  // textarea can't detach its configuration.
  const updateOverride = useCallback(
    (email: string, patch: Partial<InviteOverride>) => {
      const key = email.toLowerCase();
      setOverrides((prev) => {
        const current = prev[key] ?? { role: null, teams: null };
        return { ...prev, [key]: { ...current, ...patch } };
      });
    },
    [],
  );

  const removeEmail = useCallback(
    (email: string) => {
      const target = email.toLowerCase();
      setEmailsText((prev) =>
        prev
          .split(EMAIL_SPLIT)
          .filter((s) => s && s.toLowerCase() !== target)
          .join(" "),
      );
      // Drop the configuration too — re-typing the address later should
      // start fresh, not silently revive stale settings.
      setOverrides((prev) => {
        if (!(target in prev)) return prev;
        const { [target]: _removed, ...rest } = prev;
        return rest;
      });
      const remaining = validEmails.filter(
        (e) => e.toLowerCase() !== target,
      ).length;
      if (remaining === 0) setMode("compose");
    },
    [validEmails],
  );

  const effectiveRoleId = useCallback(
    (email: string) => overrides[email.toLowerCase()]?.role ?? composeRoleId,
    [overrides, composeRoleId],
  );

  const effectiveTeamIds = useCallback(
    (email: string) => overrides[email.toLowerCase()]?.teams ?? mainTeamIds,
    [overrides, mainTeamIds],
  );

  // Any pending invite with a per-invite role/team override. While true,
  // the compose defaults are hidden — they no longer apply uniformly.
  const hasIndividualChanges = useMemo(
    () =>
      validEmails.some((email) => {
        const o = overrides[email.toLowerCase()];
        return !!o && (o.role !== null || o.teams !== null);
      }),
    [validEmails, overrides],
  );

  const resetOverrides = useCallback(() => setOverrides({}), []);

  const canSubmit =
    !submitting && composeRoleId !== null && validEmails.length > 0;

  const handleClose = useCallback(() => {
    setMode("compose");
    setEmailsText("");
    setOverrides({});
    setMainRole(null);
    setMainTeamIds([]);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(async () => {
    if (submitting || composeRoleId === null || validEmails.length === 0)
      return;

    setSubmitting(true);
    const { succeededEmails, failures, error } = await inviteMembers(
      validEmails.map((email) => ({
        email,
        roleId: overrides[email.toLowerCase()]?.role ?? composeRoleId,
        teamIds: overrides[email.toLowerCase()]?.teams ?? mainTeamIds,
      })),
    );
    setSubmitting(false);

    if (error) {
      onError?.(error);
      return;
    }
    if (succeededEmails.length > 0) {
      onSuccess?.(succeededEmails.length);
      const succeededSet = new Set(succeededEmails.map((e) => e.toLowerCase()));
      setEmailsText((prev) =>
        prev
          .split(EMAIL_SPLIT)
          .filter((s) => s && !succeededSet.has(s.toLowerCase()))
          .join(" "),
      );
    }
    if (failures.length > 0) {
      onError?.(failures.map((f) => `${f.email}: ${f.reason}`).join("; "));
    }
    if (failures.length === 0 && succeededEmails.length > 0) handleClose();
  }, [
    validEmails,
    overrides,
    submitting,
    composeRoleId,
    mainTeamIds,
    inviteMembers,
    onSuccess,
    onError,
    handleClose,
  ]);

  const renderRow = (email: string) => (
    <Box
      direction="row"
      align="center"
      gap="sm"
      border
      rounded="md"
      surface="sunken"
      padding="sm"
    >
      <Box flex={1} minWidth={0}>
        <Text size="sm" weight="medium" truncate={1}>
          {email}
        </Text>
      </Box>
      <Box flex={1} minWidth={0}>
        <RoleSelector
          value={effectiveRoleId(email)}
          onValueChange={(next) => updateOverride(email, { role: next })}
          allowedRoleIds={allowedRoleIds}
          fill
          size="sm"
        />
      </Box>
      <Box flex={1} minWidth={0}>
        <TeamSelector
          value={effectiveTeamIds(email)}
          onValueChange={(teamIds) => updateOverride(email, { teams: teamIds })}
          placeholder="Team (optional)"
          summary
          fill
          size="sm"
        />
      </Box>
      <Button variant="ghost" size="icon-sm" onClick={() => removeEmail(email)}>
        <Icon name="x" size="sm" />
      </Button>
    </Box>
  );

  const composeBody = (
    <Box gap="md">
      <Box gap="sm">
        <Field>
          <FieldLabel>Invite by email</FieldLabel>
          <Textarea
            placeholder="Email, comma or space separated"
            value={emailsText}
            onChange={(e) => setEmailsText(e.target.value)}
            rows={3}
          />
        </Field>
        <Box direction="row" align="center" justify="between">
          <Button
            variant="outline"
            size="sm"
            disabled={validEmails.length === 0}
            onClick={() => setMode("review")}
          >
            Configure {validEmails.length} invite
            {validEmails.length === 1 ? "" : "s"}
          </Button>
          {hasIndividualChanges && (
            <Button variant="ghost" size="sm" onClick={resetOverrides}>
              Reset
            </Button>
          )}
        </Box>
      </Box>
      {!hasIndividualChanges && (
        <>
          <Box gap="sm">
            <Text size="sm" weight="medium">
              Invite as
            </Text>
            <RoleSelector
              value={composeRoleId}
              onValueChange={setMainRole}
              allowedRoleIds={allowedRoleIds}
              detailed
            />
          </Box>
          <Box gap="xs">
            <Box direction="row" align="center" justify="between">
              <Text size="sm" weight="medium">
                Add to team
              </Text>
              <Text size="xs" variant="muted">
                Optional
              </Text>
            </Box>
            <TeamSelector
              value={mainTeamIds}
              onValueChange={setMainTeamIds}
              placeholder="Select a team"
              summary
              fill
            />
          </Box>
        </>
      )}
    </Box>
  );

  const reviewBody = (
    <Box direction="column" maxHeight={420}>
      <ScrollArea>
        <Box gap="sm">
          {validEmails.map((email, index) => (
            <Box key={`${email}-${index}`}>{renderRow(email)}</Box>
          ))}
        </Box>
      </ScrollArea>
    </Box>
  );

  const body = mode === "review" ? reviewBody : composeBody;

  // One Dialog at every breakpoint — `bottomStickOnMobile` gives the
  // sheet-like bottom-anchored surface on small screens.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The popup owns its own surface and padding. */}
      <DialogPopup initialFocus={isTouchDevice ? false : undefined}>
        <DialogHeader>
          {mode === "review" ? (
            <Box
              as="button"
              type="button"
              direction="row"
              align="center"
              gap="xs"
              cursor="pointer"
              onClick={() => setMode("compose")}
            >
              <Icon name="arrow-left" size="sm" />
              <DialogTitle>Back to Invite</DialogTitle>
            </Box>
          ) : (
            <DialogTitle>Invite people</DialogTitle>
          )}
        </DialogHeader>
        {/* DialogPanel owns the body gutters and the header/footer spacing
            rules — a bare padded Box leaves no gap above the footer. */}
        <DialogPanel>{body}</DialogPanel>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            {validEmails.length > 1
              ? `Send invites (${validEmails.length})`
              : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
